type Handler = {
  callback: (...args: never[]) => void;
  isOnce: boolean;
  _fired?: boolean;
};

const MAX_SUBSCRIBERS_PER_TOPIC = 50;
const MAX_TRIGGER_DEPTH = 3;

export class PubSub {
  private events: Record<string, Handler[]> = {};
  private everTriggeredEventData: Record<string, unknown> = {};
  private triggerDepth: Record<string, number> = {};

  on(
    eventTypeParam: string,
    callback: (...args: never[]) => void,
    isOnce = false
  ): boolean {
    const eventType = eventTypeParam.toLowerCase();
    const handlers = (this.events[eventType] ??= []);

    if (handlers.some((h) => h.callback === callback)) {
      console.warn(
        `[PubSub] Duplicate handler registration prevented for event: ${eventType}`
      );
      return false;
    }

    if (handlers.length >= MAX_SUBSCRIBERS_PER_TOPIC) {
      console.error(
        `[PubSub] Subscriber limit (${MAX_SUBSCRIBERS_PER_TOPIC}) reached for event "${eventType}". ` +
          'This likely indicates a subscription leak. New handler NOT registered.'
      );
      return false;
    }

    handlers.push({ callback, isOnce });
    return true;
  }

  once(eventType: string, callback: (...args: never[]) => void): void {
    this.on(eventType, callback, true);
  }

  ever(eventTypeParam: string, callback: (data: unknown) => void): void {
    const eventType = eventTypeParam.toLowerCase();
    if (eventType in this.everTriggeredEventData) {
      try {
        callback(this.everTriggeredEventData[eventType]);
      } catch (err) {
        console.error(
          `[PubSub] ever() cached callback threw for "${eventType}":`,
          err
        );
      }
      return;
    }
    this.on(eventType, callback as (...args: never[]) => void, true);
  }

  off(eventTypeParam: string, callback: (...args: never[]) => void): void {
    const eventType = eventTypeParam.toLowerCase();
    const handlers = this.events[eventType];
    if (!handlers) return;
    this.events[eventType] = handlers.filter((h) => h.callback !== callback);
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: snapshot iteration + re-entrancy guard deliberately fused
  trigger(eventTypeParam: string, data?: unknown, ever?: boolean): void {
    const eventType = eventTypeParam.toLowerCase();

    if (ever && !(eventType in this.everTriggeredEventData)) {
      this.everTriggeredEventData[eventType] = data;
    }

    if (!this.events[eventType]?.length) return;

    const currentDepth = this.triggerDepth[eventType] ?? 0;
    if (currentDepth >= MAX_TRIGGER_DEPTH) {
      console.error(
        `[PubSub] Recursive trigger() depth limit (${MAX_TRIGGER_DEPTH}) reached for event "${eventType}". ` +
          'This indicates a handler is re-triggering its own event. Skipping to prevent stack overflow.'
      );
      return;
    }

    const eventsRef = this.events;
    const depthRef = this.triggerDepth;
    depthRef[eventType] = currentDepth + 1;

    try {
      // Snapshot prevents handlers added during trigger() from firing in this
      // pass; handler._fired prevents once() handlers from running twice if
      // another handler re-triggers the event.
      const snapshot = [...eventsRef[eventType]!];
      const handlersToRemove: Handler[] = [];

      for (const handler of snapshot) {
        if (this.events !== eventsRef) break;
        if (!this.events[eventType]?.includes(handler)) continue;
        if (handler._fired) continue;

        if (handler.isOnce) handler._fired = true;

        try {
          (handler.callback as (data: unknown) => void)(data);
        } catch (err) {
          console.error(
            `[PubSub] Subscriber threw during "${eventType}":`,
            err
          );
        }

        if (handler.isOnce) handlersToRemove.push(handler);
      }

      if (handlersToRemove.length > 0 && this.events[eventType]) {
        const toRemoveSet = new Set(handlersToRemove);
        this.events[eventType] = this.events[eventType]!.filter(
          (h) => !toRemoveSet.has(h)
        );
      }
    } finally {
      if (this.triggerDepth === depthRef) {
        depthRef[eventType] = currentDepth;
      } else {
        delete depthRef[eventType];
        if (currentDepth > 0) this.triggerDepth[eventType] = currentDepth;
      }
    }
  }

  resetPubSub(): void {
    this.events = {};
    this.everTriggeredEventData = {};
    this.triggerDepth = {};
  }
}
